/**
 * Strike-aligned volume / delta / premium profile from Legacy RTL scanner rows.
 * Uses the same aggregation rules as Unified Legacy Volume tools.
 */
import type { ScannerData } from '../types/ScannerData';
import type { DataMode } from '../types/DataMode';
import { getUnderlyingSymbol } from './filterUtils';
import {
  aggregateLegacyVolumeByStrike,
  type LegacyStrikeAggRow,
} from './legacyVolumeStrikeAggregation';
import { getGexChainCandidates, resolveSymbol } from '../services/symbolPricingService';

export type RtlLegacyProfileMetric = 'volume' | 'delta' | 'premium';

export interface RtlLegacyProfileLevel {
  strike: number;
  chartPrice: number;
  magnitude: number;
  netValue: number;
  direction: 'bullish' | 'bearish' | 'neutral';
  boughtPower: number;
  soldPower: number;
}

export function rtlLegacyUnderlyingCandidates(symbol: string): string[] {
  const raw = String(symbol || '').trim().toUpperCase().replace(/^\^/, '');
  const set = new Set<string>();
  if (raw) set.add(raw);
  for (const c of getGexChainCandidates(symbol)) {
    set.add(c.toUpperCase().replace(/^\^/, ''));
  }
  const resolved = resolveSymbol(symbol);
  if (resolved?.gexUnderlying) {
    set.add(resolved.gexUnderlying.toUpperCase().replace(/^\^/, ''));
  }
  if (resolved?.displaySymbol) {
    set.add(resolved.displaySymbol.toUpperCase().replace(/^\^/, ''));
  }
  return Array.from(set);
}

export function filterRtlLegacyRowsForSymbol(
  rows: ScannerData[],
  symbol: string,
): ScannerData[] {
  if (!rows?.length) return [];
  const allowed = new Set(rtlLegacyUnderlyingCandidates(symbol));
  return rows.filter((row) => {
    const u = getUnderlyingSymbol(row).toUpperCase().replace(/^\^/, '');
    if (allowed.has(u)) return true;
    if (u.endsWith('W') && allowed.has(u.slice(0, -1))) return true;
    if (allowed.has(`${u}W`)) return true;
    return false;
  });
}

function metricValues(row: LegacyStrikeAggRow, metric: RtlLegacyProfileMetric): {
  net: number;
  bought: number;
  sold: number;
} {
  const boughtCalls = row.boughtCalls;
  const soldCalls = row.soldCalls;
  const boughtPuts = row.boughtPuts;
  const soldPuts = row.soldPuts;

  if (metric === 'premium') {
    const bought =
      row.boughtCallPremium + row.boughtPutPremium;
    const sold = row.soldCallPremium + row.soldPutPremium;
    return { net: row.netPremium, bought, sold };
  }

  if (metric === 'delta') {
    const bought = boughtCalls + soldPuts;
    const sold = soldCalls + boughtPuts;
    return { net: row.netDirectionalVolume, bought, sold };
  }

  const bought = boughtCalls + boughtPuts;
  const sold = soldCalls + soldPuts;
  return { net: bought - sold, bought, sold };
}

export function buildRtlLegacyStrikeProfile(
  rows: ScannerData[],
  symbol: string,
  metric: RtlLegacyProfileMetric,
  dataMode: DataMode,
  spotPrice: number,
  nearSpotPct = 0.12,
  chartScale = 1,
): RtlLegacyProfileLevel[] {
  const filtered = filterRtlLegacyRowsForSymbol(rows, symbol);
  if (!filtered.length || spotPrice <= 0) return [];

  const agg = aggregateLegacyVolumeByStrike(filtered, dataMode, false);
  const band = spotPrice * nearSpotPct;
  const lo = spotPrice - band;
  const hi = spotPrice + band;

  const levels: RtlLegacyProfileLevel[] = [];
  for (const row of agg) {
    const strike = parseFloat(row.strike);
    if (!Number.isFinite(strike) || strike <= 0) continue;
    if (strike < lo || strike > hi) continue;

    const { net, bought, sold } = metricValues(row, metric);
    const magnitude = Math.max(Math.abs(net), bought, sold);
    if (magnitude <= 0) continue;

    let direction: RtlLegacyProfileLevel['direction'] = 'neutral';
    if (net > 0) direction = 'bullish';
    else if (net < 0) direction = 'bearish';

    levels.push({
      strike,
      chartPrice: strike * chartScale,
      magnitude,
      netValue: net,
      direction,
      boughtPower: bought,
      soldPower: sold,
    });
  }

  levels.sort((a, b) => a.strike - b.strike);
  return levels;
}
